import {
  AlertTriangle,
  Check,
  CheckCircle2,
  ChevronRight,
  Copy,
  Hourglass,
  Link2,
} from 'lucide-react';
import PropTypes from 'prop-types';
import React from 'react';

import { getTaskReasonById } from 'common-src/models/PatientTask';

import useAlert from 'src/hooks/useAlert';

import { PRIORITY_BADGE_COPY, TASK_DISPLAY_STATUS, TASK_PRIORITY, teamMeta } from '../../constants';
import {
  copyTextToClipboard,
  formatDue,
  getPatient,
  getTaskDeepLinkUrl,
  getTaskDisplayStatus,
  getTaskDisplayStatusLabel,
  getTaskDisplayTitle,
  isAssessmentTask,
  isLinkedTask,
  isMessageTask,
  isTaskBlockedStatus,
  isTaskEscalatedStatus,
  isTaskUnableToComplete,
  isWorkflowBlockedTask,
} from '../../helpers';
import { Badge } from '../Badge';
import styles from './TaskRow.module.scss';

const TaskRow = ({
  task,
  compact,
  onSelect,
  selected,
  onAssignToMe,
  isAssigning,
  isLinkedChild,
  isSubtaskChild,
  isSubtask,
  parentTitle,
  linkedParentTitle,
  linkedChildCount,
  subtaskChildCount,
  delegatedSubtaskMeta,
  linkedExpanded,
  onToggleLinked,
  selectionMode,
  isSelected,
  onToggleSelect,
}) => {
  const { showAlert, AlertType } = useAlert();

  if (!task) return null;

  const patient = task?.patient ?? getPatient(task?.patientId);
  const due = task?.due ? formatDue(task.due) : { text: '—', urgent: false };
  const displayStatus = getTaskDisplayStatus(task);
  const displayStatusLabel = getTaskDisplayStatusLabel(task);
  const isBlocked = isTaskBlockedStatus(task);
  const isEscalated = isTaskEscalatedStatus(task);
  const isCompleted = displayStatus === TASK_DISPLAY_STATUS.Done;
  const isUnableToComplete = isTaskUnableToComplete(task);
  const isLinked = isLinkedTask(task);
  const isBlockedByDependencies = isWorkflowBlockedTask(task);
  const taskPriority = task?.priority ?? TASK_PRIORITY.Low;
  const ownerLabel = `${task?.owner || ''}`.trim();
  const isUnassigned = !ownerLabel || ownerLabel.toLowerCase() === 'unassigned';
  const taskIsMessage = isMessageTask(task);
  const taskIsAssessment = isAssessmentTask(task);
  const taskTitle = getTaskDisplayTitle(task);
  const taskReasonLabel =
    `${task?.reason || ''}`.trim() || getTaskReasonById(task?.reasonId)?.label || '';
  const taskDescription = `${task?.taskDescription || task?.task_description || ''}`.trim();
  const fallbackMetaLabel = `${patient?.name || '-'} -> ${task?.owner || 'Unassigned'}`;
  const teamInfo = teamMeta[task?.team] || null;
  const teamIcon = task?.team === 'rcm' ? '◇' : teamInfo?.icon;
  const teamLabel = teamInfo?.label ? `${teamInfo.label} team` : null;
  const subtaskAssignmentOwner = ownerLabel || 'unassigned';
  const showPriorityBorder = !isCompleted;
  const priorityBorderClass = isSubtask
    ? styles.rowPrioritySubtask
    : isLinked
      ? styles.rowPriorityLinked
      : styles[`rowPriority${taskPriority}`] || '';
  const showCriticalIcon =
    !isLinked &&
    !isBlocked &&
    showPriorityBorder &&
    taskPriority === TASK_PRIORITY.Critical &&
    !taskIsMessage &&
    !taskIsAssessment;
  const taskDeepLinkUrl = getTaskDeepLinkUrl(task);

  const handleCopyTaskLink = async (event) => {
    event.stopPropagation();
    if (!taskDeepLinkUrl) return;

    try {
      const didCopy = await copyTextToClipboard(taskDeepLinkUrl);
      if (!didCopy) throw new Error('Copy failed');
      showAlert(AlertType.Success, '', 'Task link copied to clipboard.');
    } catch (error) {
      showAlert(AlertType.Error, '', 'Unable to copy task link.');
    }
  };

  const hasDelegatedSubtasks = delegatedSubtaskMeta?.hasSubtasks;
  const allDelegatedSubtasksComplete = delegatedSubtaskMeta?.allDone;
  const activeSubtaskChildCount = delegatedSubtaskMeta?.openCount ?? subtaskChildCount;
  const totalChildCount = linkedChildCount + activeSubtaskChildCount;
  const showLinkedToggle = !compact && totalChildCount > 0 && typeof onToggleLinked === 'function';
  const childToggleLabel = [
    linkedChildCount > 0
      ? `${linkedChildCount} linked ${linkedChildCount === 1 ? 'task' : 'tasks'}`
      : null,
    activeSubtaskChildCount > 0
      ? `${activeSubtaskChildCount} sub-${activeSubtaskChildCount === 1 ? 'task' : 'tasks'}`
      : null,
  ]
    .filter(Boolean)
    .join(', ');
  const showLinkedChildIcon = linkedChildCount > 0 && subtaskChildCount === 0;
  const showSubtaskChildIcon = activeSubtaskChildCount > 0 && linkedChildCount === 0;
  const showChildCountStatus =
    !isBlocked && !isEscalated && (linkedChildCount > 0 || activeSubtaskChildCount > 0);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => {
        if (selectionMode) {
          onToggleSelect?.(task.id);
        } else {
          onSelect?.(task);
        }
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          if (selectionMode) {
            onToggleSelect?.(task.id);
          } else {
            onSelect?.(task);
          }
        }
      }}
      className={[
        styles.row,
        compact ? styles.compact : '',
        isCompleted ? styles.completedRow : '',
        isUnableToComplete ? styles.unableToCompleteRow : '',
        isCompleted && isSubtask ? styles.completedSubtaskRow : '',
        isLinkedChild || isSubtaskChild ? styles.linkedChildRow : '',
        isSubtaskChild ? styles.subtaskChildRow : '',
        selected ? styles.selected : '',
        !compact && showPriorityBorder ? priorityBorderClass : '',
      ].join(' ')}
    >
      {selectionMode ? (
        <input
          type="checkbox"
          className={styles.selectionCheckbox}
          checked={!!isSelected}
          onChange={(event) => {
            event.stopPropagation();
            onToggleSelect?.(task.id);
          }}
          onClick={(event) => event.stopPropagation()}
          aria-label={`Select task: ${taskTitle}`}
        />
      ) : isCompleted ? (
        <div
          className={[
            styles.checkBox,
            taskPriority === TASK_PRIORITY.Critical ? styles.checkCritical : '',
            isBlocked ? styles.checkFlagged : '',
            isUnableToComplete ? styles.checkUnableToComplete : '',
          ].join(' ')}
        >
          <Check className={styles.checkIcon} strokeWidth={2.4} />
        </div>
      ) : isSubtask ? (
        <span className={styles.subtaskIcon} aria-hidden="true">
          <Hourglass className={styles.subtaskIconGlyph} strokeWidth={1.8} />
        </span>
      ) : isLinked ? (
        <span className={styles.linkedIcon} aria-hidden="true">
          <Link2 className={styles.linkedIconGlyph} strokeWidth={1.8} />
        </span>
      ) : taskIsMessage ? (
        <span className={styles.messageIcon} aria-hidden="true" />
      ) : taskIsAssessment ? (
        <span className={styles.assessmentIcon} aria-hidden="true" />
      ) : showCriticalIcon ? (
        <span className={styles.criticalIcon} aria-hidden="true">
          <AlertTriangle className={styles.criticalIconGlyph} strokeWidth={2.2} />
        </span>
      ) : (
        <div
          className={[
            styles.checkBox,
            taskPriority === TASK_PRIORITY.Critical ? styles.checkCritical : '',
            isBlocked ? styles.checkFlagged : '',
          ].join(' ')}
        />
      )}
      <div className={styles.main}>
        <div className={styles.titleRow}>
          <span className={`${styles.title} ${isCompleted ? styles.completedTitle : ''}`}>
            {taskTitle}
          </span>
          {isSubtask ? (
            <span className={styles.subtaskAssignment}>
              <span className={styles.subtaskAssignmentArrow}>&rarr;</span>
              {teamLabel ? (
                <>
                  <span className={styles.subtaskAssignmentTeam}>
                    <span className={styles.subtaskAssignmentTeamIcon}>{teamIcon}</span>
                    <span>{teamLabel}</span>
                  </span>
                  <span className={styles.subtaskAssignmentDivider}>·</span>
                </>
              ) : null}
              <span className={styles.subtaskAssignmentOwner}>{subtaskAssignmentOwner}</span>
            </span>
          ) : null}
          {task.mustHave2026 && (
            <Badge variant="edge" className={styles.edgeBadge}>
              EDGE
            </Badge>
          )}
        </div>
        {!compact && isLinked && linkedParentTitle && !isSubtask ? (
          <div className={styles.linkedFromRow}>{`from: ${linkedParentTitle}`}</div>
        ) : null}
        {!compact && !isSubtask && taskReasonLabel ? (
          <div className={styles.reasonRow}>{taskReasonLabel}</div>
        ) : null}
        {task.caption && (
          <div className={`${styles.caption} ${compact ? styles.captionCompact : ''}`}>
            {task.caption}
          </div>
        )}
        {!compact && <div className={styles.metaRow}>{taskDescription || fallbackMetaLabel}</div>}
      </div>
      {!compact && (
        <>
          <div className={styles.assignCell}>
            {isUnassigned && !selectionMode ? (
              <button
                type="button"
                className={styles.assignButton}
                disabled={isAssigning}
                onClick={(event) => {
                  event.stopPropagation();
                  onAssignToMe?.(task.id);
                }}
              >
                {isAssigning ? 'Assigning...' : 'Assign to me'}
              </button>
            ) : null}
          </div>
          <div className={styles.statusCell}>
            {isBlocked ? (
              <Badge variant="critical">Blocked</Badge>
            ) : isEscalated ? (
              <Badge variant="escalated">Escalated</Badge>
            ) : showChildCountStatus ? (
              <Badge className={styles.childCountBadge}>
                {linkedChildCount > 0 ? (
                  <span className={styles.childCountBadgeItem}>
                    <Link2 className={styles.childCountBadgeIcon} strokeWidth={1.8} />
                    {linkedChildCount}
                  </span>
                ) : null}
                {linkedChildCount > 0 && activeSubtaskChildCount > 0 ? (
                  <span className={styles.childCountBadgeDivider}>·</span>
                ) : null}
                {activeSubtaskChildCount > 0 ? (
                  <span className={styles.childCountBadgeItem}>
                    <Hourglass className={styles.childCountBadgeIcon} strokeWidth={1.8} />
                    {activeSubtaskChildCount}
                  </span>
                ) : null}
              </Badge>
            ) : (
              <Badge variant={isUnableToComplete ? 'orange' : 'default'}>
                {displayStatusLabel}
              </Badge>
            )}
          </div>
          <div className={styles.priorityCell}>
            <span
              className={`${styles.priorityBadge} ${styles[`priorityBadge${taskPriority}`] || ''}`}
            >
              {PRIORITY_BADGE_COPY[taskPriority] ?? taskPriority}
            </span>
            <button
              type="button"
              className={styles.copyButton}
              onClick={handleCopyTaskLink}
              disabled={!taskDeepLinkUrl}
              aria-label="Copy task link"
              title="Copy task link"
            >
              <Copy className={styles.copyIcon} strokeWidth={1.8} />
            </button>
          </div>
        </>
      )}
      <span className={`${styles.dueText} ${due.urgent ? styles.dueUrgent : ''}`}>{due.text}</span>
      {!compact && isLinked && linkedParentTitle ? (
        <div className={styles.waitingOnRow}>
          <Link2 className={styles.waitingOnIcon} strokeWidth={1.8} />
          <span className={styles.waitingOnLabel}>
            {isBlockedByDependencies ? 'Blocked by:' : 'Waiting on:'}
          </span>
          <span className={styles.waitingOnTitle}>{linkedParentTitle}</span>
        </div>
      ) : null}
      {!compact && isSubtask && parentTitle ? (
        <div className={styles.subtaskOfRow}>
          <Hourglass className={styles.subtaskOfIcon} strokeWidth={1.8} />
          <span className={styles.subtaskOfTitle}>{`Sub-task of: ${parentTitle}`}</span>
        </div>
      ) : null}
      {!compact &&
      !isSubtask &&
      !isCompleted &&
      hasDelegatedSubtasks &&
      allDelegatedSubtasksComplete ? (
        <div className={styles.parentReadyRow}>
          <CheckCircle2 className={styles.parentReadyIcon} strokeWidth={1.8} />
          <span className={styles.parentReadyTitle}>
            All delegated sub-tasks complete · ready to continue
          </span>
        </div>
      ) : null}
      {showLinkedToggle ? (
        <button
          type="button"
          className={styles.linkedToggle}
          onClick={(event) => {
            event.stopPropagation();
            onToggleLinked?.(task.id);
          }}
        >
          <ChevronRight
            className={`${styles.linkedToggleChevron} ${linkedExpanded ? styles.linkedToggleChevronOpen : ''}`}
            strokeWidth={1.8}
          />
          {showLinkedChildIcon ? (
            <Link2 className={styles.linkedToggleIcon} strokeWidth={1.8} />
          ) : null}
          {showSubtaskChildIcon ? (
            <Hourglass className={styles.linkedToggleIcon} strokeWidth={1.8} />
          ) : null}
          <span>{childToggleLabel}</span>
        </button>
      ) : null}
    </div>
  );
};

TaskRow.propTypes = {
  task: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    patient: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    }),
    patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    due: PropTypes.string,
    priority: PropTypes.string,
    status: PropTypes.number,
    team: PropTypes.string,
    owner: PropTypes.string,
    reason: PropTypes.string,
    taskDescription: PropTypes.string,
    task_description: PropTypes.string,
    typeId: PropTypes.number,
    reasonId: PropTypes.number,
    title: PropTypes.string,
    assessmentType: PropTypes.string,
    assessmentVariant: PropTypes.string,
    mustHave2026: PropTypes.bool,
    caption: PropTypes.string,
    isVisible: PropTypes.bool,
    dependency: PropTypes.object,
  }),
  compact: PropTypes.bool,
  onSelect: PropTypes.func,
  selected: PropTypes.bool,
  onAssignToMe: PropTypes.func,
  isAssigning: PropTypes.bool,
  isLinkedChild: PropTypes.bool,
  isSubtaskChild: PropTypes.bool,
  isSubtask: PropTypes.bool,
  parentTitle: PropTypes.string,
  linkedParentTitle: PropTypes.string,
  linkedChildCount: PropTypes.number,
  subtaskChildCount: PropTypes.number,
  delegatedSubtaskMeta: PropTypes.shape({
    totalCount: PropTypes.number,
    openCount: PropTypes.number,
    doneCount: PropTypes.number,
    hasSubtasks: PropTypes.bool,
    waitingOnSubtasks: PropTypes.bool,
    allDone: PropTypes.bool,
    openSubtasks: PropTypes.arrayOf(PropTypes.object),
  }),
  linkedExpanded: PropTypes.bool,
  onToggleLinked: PropTypes.func,
  selectionMode: PropTypes.bool,
  isSelected: PropTypes.bool,
  onToggleSelect: PropTypes.func,
};

export default TaskRow;
